import React, { Component } from 'react';
import { FileTextOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { LOADER_RED } from '../../utils/constant';
import SmartEmrAudioPlayer from './SmartEmrAudioPlayer';

/**
 * Renders the media attached to a Smart EMR entry.
 *
 * A Smart EMR entry can originate from either:
 *  - a voice recording  -> plays back the audio (audio_url / audio_path)
 *  - uploaded images     -> opens the image(s) in a viewer modal (file_url, an array of URLs)
 *
 * Only one of these is ever present on a given entry. `file_url` is always an
 * array (one or more image URLs). This component picks the right UI so the call
 * sites (MySmart EMR Log, single screen, history modal) don't each have to
 * branch on the source type.
 *
 * Props:
 *  - item (required) the smart EMR data object (may contain audio_url/audio_path/file_url)
 */
class SmartEmrMedia extends Component {
  constructor(props) {
    super(props);
    this.state = { showFileModal: false };
  }

  // Normalise file_url to an array (tolerates a single string from older data)
  getFileUrls = () => {
    const raw = this.props.item ? this.props.item.file_url : null;
    if (!raw) return [];
    return Array.isArray(raw) ? raw.filter(Boolean) : [raw];
  };

  getFileName = (url) => {
    try {
      const clean = String(url).split('?')[0].split('#')[0];
      const name = clean.substring(clean.lastIndexOf('/') + 1);
      return decodeURIComponent(name) || 'Uploaded file';
    } catch (e) {
      return 'Uploaded file';
    }
  };

  isPdf = (url) => String(url).split('?')[0].split('#')[0].split('.').pop().toLowerCase() === 'pdf';

  openFile = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    this.setState({ showFileModal: true });
  };

  closeFile = () => this.setState({ showFileModal: false });

  render() {
    const { item } = this.props;
    if (!item) return null;

    const audioUrl = item.audio_url || item.audio_path || null;

    // Recording entry -> audio player
    if (audioUrl) {
      return <SmartEmrAudioPlayer audioUrl={audioUrl} />;
    }

    // File-upload entry -> link that opens the image(s) in a viewer modal
    const fileUrls = this.getFileUrls();
    if (fileUrls.length > 0) {
      const label = fileUrls.length > 1 ? `View files (${fileUrls.length})` : 'View file';

      return (
        <>
          <a
            href={fileUrls[0]}
            onClick={this.openFile}
            title={label}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#d24350', fontWeight: 600, fontSize: 13 }}
          >
            <FileTextOutlined />
            <span>{label}</span>
          </a>

          <Modal
            title="MySmart EMR — Uploaded Files"
            visible={this.state.showFileModal}
            footer={false}
            width={850}
            style={{ top: 20 }}
            onCancel={this.closeFile}
            zIndex={10002}
          >
            {fileUrls.map((url, index) => (
              <div className="row" key={`${url}-${index}`}>
                <div className="col-12 my-3 text-center">
                  {this.isPdf(url) ? (
                    <iframe
                      src={url}
                      title={`smart-emr-file-${index}`}
                      frameBorder="0"
                      scrolling="no"
                      style={{ overflow: 'hidden' }}
                      width="100%"
                      height="700"
                    >
                      {LOADER_RED}
                    </iframe>
                  ) : (
                    <img src={url} alt={this.getFileName(url)} style={{ width: '100%' }} />
                  )}
                </div>
              </div>
            ))}
          </Modal>
        </>
      );
    }

    return null;
  }
}

export default SmartEmrMedia;
